import { useEffect, useState } from 'react';
import {
    useOrthodontistFeatureFlags,
    useToggleOrthodontistFeatureFlag
} from '../../../../hooks/useOrthodontistFeatureFlags';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UDToggle } from '../../users/UDToggle';

interface OrthodontistFeatureSettingsSectionProps {
    orthodontistId: string;
}

export function OrthodontistFeatureSettingsSection({
    orthodontistId
}: OrthodontistFeatureSettingsSectionProps) {
    const { showToast } = useToast();
    const { features, isLoading, isError, error } = useOrthodontistFeatureFlags(orthodontistId);
    const toggleFeature = useToggleOrthodontistFeatureFlag(orthodontistId);
    const [pendingFeature, setPendingFeature] = useState<string | null>(null);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load feature settings'), 'error');
        }
    }, [isError, error, showToast]);

    const handleToggle = async (feature: string, enabled: boolean) => {
        setPendingFeature(feature);
        try {
            await toggleFeature.mutateAsync({ feature, enabled });
            showToast(enabled ? `${feature} enabled` : `${feature} disabled`, 'success');
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to update feature'), 'error');
        } finally {
            setPendingFeature(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5 flex flex-col gap-5 shrink-0">
            <div>
                <h3 className="text-sm font-bold text-gray-800">Feature settings</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Feature flags are per-org and read by this orthodontist's dashboard.
                </p>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            ) : features.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">
                    No features defined for this organization.
                </p>
            ) : (
                <div className="divide-y divide-gray-100">
                    {features.map((feature) => (
                        <div key={feature.key} className="py-3">
                            <UDToggle
                                title={feature.title}
                                desc={feature.description}
                                on={feature.enabled}
                                disabled={pendingFeature === feature.key}
                                onChange={(next) => void handleToggle(feature.key, next)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
